<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <!-- Alert -->
    <div v-if="alert.show" 
         :class="[
           'mb-6 p-4 rounded-md border',
           alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : '',
           alert.type === 'danger' ? 'bg-red-50 border-red-200 text-red-800' : '',
           alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : ''
         ]">
      {{ alert.message }}
    </div>

    <!-- Page Header -->
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">إدارة الأهداف</h1>
        <p class="mt-2 text-sm text-gray-700">إدارة أهداف الدارسين</p>
      </div>
      <div class="mt-4 sm:mt-0 sm:mr-16 sm:flex-none">
        <button
          @click="openAddModal"
          class="px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor/90 transition-colors">
          إضافة هدف جديد
        </button>
      </div>
    </div>

    <!-- Search and Filter Section -->
    <div class="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      <div class="relative">
        <input
          v-model="searchTerm"
          type="text"
          placeholder="البحث في الأهداف..."
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-3"
        />
      </div>
      <div class="relative">
        <select
          v-model="filterActive"
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        >
          <option :value="null">جميع الأهداف</option>
          <option :value="true">أهداف نشطة فقط</option>
          <option :value="false">أهداف غير نشطة</option>
        </select>
      </div>
      <div class="relative">
        <select
          v-model="filterMandatory"
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        >
          <option :value="null">جميع الأهداف</option>
          <option :value="true">أهداف إلزامية فقط</option>
          <option :value="false">أهداف غير إلزامية</option>
        </select>
      </div>
      <div class="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg flex items-center">
        إجمالي الأهداف: {{ goals.length }}
      </div>
    </div>

    <!-- Goals Table -->
    <div class="mt-8 flex flex-col">
      <div class="sm:flex-auto">
        <h1 class="text-lg font-medium text-gray-900">قائمة الأهداف</h1>
      </div>

      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-gray-600">جاري التحميل...</p>
      </div>

      <div v-else-if="filteredGoals.length === 0" class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <p class="mt-2 text-gray-600">
          {{ searchTerm || filterActive !== null || filterMandatory !== null ? 'لا توجد أهداف تطابق البحث' : 'لا توجد أهداف مسجلة' }}
        </p>
      </div>

      <div v-else class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table class="min-w-full divide-y divide-customHafadhColor">
              <thead class="bg-customHafadhColor text-center">
                <tr>
                  <th class="py-3.5 pr-4 pl-3 text-right text-sm font-semibold text-gray-900 sm:pr-6">#</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">اسم الهدف</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">وصف الهدف</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">كود الهدف</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">قيمة الهدف</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">الحالة</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">إلزامي</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">تعديل</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">حذف</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr v-for="(goal, index) in filteredGoals" :key="goal.id">
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm text-gray-900 sm:pr-6">{{ index + 1 }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">{{ goal.goalName || '-' }}</td>
                  <td class="px-3 py-4 text-sm text-gray-500">{{ goal.goalDesc || '-' }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ goal.goalCode || '-' }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ goal.goalValue ?? '-' }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm">
                    <span 
                      :class="[
                        'px-2 py-1 rounded-full text-xs font-medium',
                        goal.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      ]">
                      {{ goal.isActive ? 'نشط' : 'غير نشط' }}
                    </span>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm">
                    <span 
                      :class="[
                        'px-2 py-1 rounded-full text-xs font-medium',
                        goal.isMandatory ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                      ]">
                      {{ goal.isMandatory ? 'إلزامي' : 'غير إلزامي' }}
                    </span>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button class="enabled-button" @click="openEditModal(goal)">تعديل</button>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button 
                      class="text-red-600 hover:text-red-800" 
                      @click="handleDelete(goal)">
                      حذف
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.totalPages > 1" class="mt-6 flex items-center justify-between">
        <div class="text-sm text-gray-700">
          عرض {{ pagination.from }} إلى {{ pagination.to }} من {{ pagination.total }} نتائج
        </div>
        <div class="flex gap-2">
          <button
            @click="changePage(pagination.currentPage - 1)"
            :disabled="pagination.currentPage === 1"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            السابق
          </button>
          <button
            @click="changePage(pagination.currentPage + 1)"
            :disabled="pagination.currentPage === pagination.totalPages"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            التالي
          </button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="popup" dir="rtl">
      <div class="popup-content" style="max-width: 600px;">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
          {{ editMode ? 'تعديل الهدف' : 'إضافة هدف جديد' }}
        </h3>
        
        <form @submit.prevent="handleSave">
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label for="goal_name" class="block text-sm font-medium text-gray-700 mb-2">اسم الهدف</label>
              <input
                v-model="formData.goal_name"
                type="text"
                id="goal_name"
                placeholder="أدخل اسم الهدف"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label for="goal_desc" class="block text-sm font-medium text-gray-700 mb-2">وصف الهدف</label>
              <textarea
                v-model="formData.goal_desc"
                id="goal_desc"
                rows="3"
                placeholder="أدخل وصف الهدف"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              ></textarea>
            </div>
            <div>
              <label for="goal_code" class="block text-sm font-medium text-gray-700 mb-2">كود الهدف</label>
              <input
                v-model="formData.goal_code"
                type="text"
                id="goal_code"
                placeholder="أدخل كود الهدف (مثل: SELF_REVIEW)"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label for="goal_value" class="block text-sm font-medium text-gray-700 mb-2">قيمة الهدف</label>
              <input
                v-model.number="formData.goal_value"
                type="number"
                id="goal_value"
                min="0"
                placeholder="أدخل قيمة الهدف"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div class="flex items-center">
              <input
                v-model="formData.is_active"
                type="checkbox"
                id="is_active"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label for="is_active" class="mr-2 block text-sm text-gray-900">
                نشط
              </label>
            </div>
            <div class="flex items-center">
              <input
                v-model="formData.is_mandatory"
                type="checkbox"
                id="is_mandatory"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label for="is_mandatory" class="mr-2 block text-sm text-gray-900">
                إلزامي
              </label>
            </div>
          </div>

          <div class="mt-6 flex gap-3 justify-end">
            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
              إلغاء
            </button>
            <button
              type="submit"
              :disabled="saving"
              class="px-4 py-2 bg-customHafadhColor text-white rounded-md text-sm font-medium hover:bg-customHafadhColor/90 disabled:opacity-50">
              {{ saving ? 'جاري الحفظ...' : (editMode ? 'تحديث' : 'إضافة') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import axios from 'axios'
import url from '@/url.js'

export default defineComponent({
  name: 'GoalsManagement',
  data() {
    return {
      goals: [],
      loading: false,
      saving: false,
      searchTerm: '',
      filterActive: null,
      filterMandatory: null,
      showModal: false,
      editMode: false,
      formData: {
        id: null,
        goal_name: '',
        goal_desc: '',
        goal_code: '',
        goal_value: null,
        is_active: true,
        is_mandatory: false,
      },
      alert: {
        show: false,
        type: 'success',
        message: '',
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        total: 0,
        from: 0,
        to: 0,
      },
    }
  },
  computed: {
    filteredGoals() {
      let filtered = this.goals

      if (this.searchTerm) {
        const search = this.searchTerm.toLowerCase()
        filtered = filtered.filter(goal => 
          goal.goalName?.toLowerCase().includes(search) ||
          goal.goalDesc?.toLowerCase().includes(search) ||
          goal.goalCode?.toLowerCase().includes(search)
        )
      }

      if (this.filterActive !== null) {
        filtered = filtered.filter(goal => goal.isActive === this.filterActive)
      }

      if (this.filterMandatory !== null) {
        filtered = filtered.filter(goal => goal.isMandatory === this.filterMandatory)
      }

      return filtered
    }
  },
  watch: {
    searchTerm() {
      this.pagination.currentPage = 1
    },
    filterActive() {
      this.pagination.currentPage = 1
      this.loadGoals()
    },
    filterMandatory() {
      this.pagination.currentPage = 1
      this.loadGoals()
    },
  },
  mounted() {
    this.loadGoals()
  },
  methods: {
    async loadGoals() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          size: 20,
        }
        if (this.searchTerm) {
          params.search = this.searchTerm
        }
        if (this.filterActive !== null) {
          params.is_active = this.filterActive
        }
        if (this.filterMandatory !== null) {
          params.is_mandatory = this.filterMandatory
        }

        const { data } = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.goals_list,
          {
            params,
            headers: { 'X-Authorization': localStorage.getItem('token') }
          }
        )

        // Handle nested response structure
        if (data.result && data.result.data) {
          if (Array.isArray(data.result.data)) {
            this.goals = data.result.data
          } else if (data.result.data.data && Array.isArray(data.result.data.data)) {
            this.goals = data.result.data.data
            if (data.result.data.pagination) {
              this.pagination = {
                currentPage: data.result.data.pagination.current_page || 1,
                totalPages: data.result.data.pagination.total_pages || 1,
                total: data.result.data.pagination.total || 0,
                from: data.result.data.pagination.from || 0,
                to: data.result.data.pagination.to || 0,
              }
            }
          } else {
            this.goals = []
          }
        } else {
          this.goals = []
        }
      } catch (error) {
        console.error('Failed to load goals', error)
        this.showAlert('danger', 'فشل في تحميل الأهداف')
        this.goals = []
      } finally {
        this.loading = false
      }
    },
    openAddModal() {
      this.editMode = false
      this.formData = {
        id: null,
        goal_name: '',
        goal_desc: '',
        goal_code: '',
        goal_value: null,
        is_active: true,
        is_mandatory: false,
      }
      this.showModal = true
    },
    openEditModal(goal) {
      this.editMode = true
      this.formData = {
        id: goal.id,
        goal_name: goal.goalName || '',
        goal_desc: goal.goalDesc || '',
        goal_code: goal.goalCode || '',
        goal_value: goal.goalValue ?? null,
        is_active: goal.isActive ?? true,
        is_mandatory: goal.isMandatory ?? false,
      }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.editMode = false
      this.formData = {
        id: null,
        goal_name: '',
        goal_desc: '',
        goal_code: '',
        goal_value: null,
        is_active: true,
        is_mandatory: false,
      }
    },
    async handleSave() {
      this.saving = true
      try {
        const payload = {
          goal_name: this.formData.goal_name || null,
          goal_desc: this.formData.goal_desc || null,
          goal_code: this.formData.goal_code || null,
          goal_value: this.formData.goal_value || null,
          is_active: this.formData.is_active,
          is_mandatory: this.formData.is_mandatory,
        }

        if (this.editMode) {
          await axios.put(
            process.env.VUE_APP_SERVICE_URL + url.goals_update + this.formData.id,
            payload,
            { headers: { 'X-Authorization': localStorage.getItem('token') } }
          )
          this.showAlert('success', 'تم تحديث الهدف بنجاح')
        } else {
          await axios.post(
            process.env.VUE_APP_SERVICE_URL + url.goals_create,
            payload,
            { headers: { 'X-Authorization': localStorage.getItem('token') } }
          )
          this.showAlert('success', 'تم إضافة الهدف بنجاح')
        }

        this.closeModal()
        this.loadGoals()
      } catch (error) {
        console.error('Failed to save goal', error)
        const message = error.response?.data?.result?.mesAr || error.response?.data?.mesAr || 'فشل في حفظ الهدف'
        this.showAlert('danger', message)
      } finally {
        this.saving = false
      }
    },
    async handleDelete(goal) {
      const goalName = goal.goalName || goal.goalCode || 'الهدف'
      if (!confirm(`هل أنت متأكد من حذف الهدف "${goalName}"؟`)) {
        return
      }

      try {
        await axios.delete(
          process.env.VUE_APP_SERVICE_URL + url.goals_delete + goal.id,
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        )
        this.showAlert('success', 'تم حذف الهدف بنجاح')
        this.loadGoals()
      } catch (error) {
        console.error('Failed to delete goal', error)
        const message = error.response?.data?.result?.mesAr || error.response?.data?.mesAr || 'فشل في حذف الهدف'
        this.showAlert('danger', message)
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.pagination.currentPage = page
        this.loadGoals()
      }
    },
    showAlert(type, message) {
      this.alert = { show: true, type, message }
      setTimeout(() => {
        this.alert.show = false
      }, 5000)
    },
  },
})
</script>

<style scoped>
.popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.popup-content {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.enabled-button {
  color: #3b82f6;
  font-weight: 500;
  cursor: pointer;
}

.enabled-button:hover {
  color: #2563eb;
  text-decoration: underline;
}
</style>

